import { useMemo, useState, useEffect, useCallback } from 'react';
import Accordion from './Accordion';
import FieldRenderer from './FieldRenderer';
import { useShake } from '../../ShakeProvider';
import { validateVersionName, validateFormats, keyBelongsToWorkspace } from '../../utils/validation';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentGroupId } from '@/store/selectors';
import MultiSelectDropdown from './MultiSelectDropdown';
import {
  getJobRawValue,
  buildOptionMediaValue,
  buildProjectMediaValue,
} from '../../utils/fieldMapping';

interface FieldSpec {
  type: string;
  options?: Array<Record<string, unknown>>;
  value?: string;
  max_length?: number;
  placeholder?: string;
  index?: number;
}

type TemplateFields = Record<string, Record<string, FieldSpec>>;
type FormValues = Record<string, Record<string, unknown>>;

export default function TemplateForm() {
  const {
    selectedTemplate: tpl,
    selectedJob,
    view,
    goBack,
    createJob,
    createCopyFrom,
    saveDraft,
  } = useShake();
  const currentGroupId = useAppSelector(selectCurrentGroupId);

  const readOnly = view === 'job-view';

  // ---------- Basic state ----------
  const allFormats: string[] = tpl.formats || [];
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [nameError, setNameError] = useState<string | null>(null);
  const [formatsError, setFormatsError] = useState<string | null>(null);

  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);

  const templateFields: TemplateFields = useMemo(() => tpl.fields || {}, [tpl]);

  // Stable display order: categories alphabetical, fields by explicit index then alphabetical
  const sortedCategories = useMemo(
    () =>
      Object.entries(templateFields)
        .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        .map(([cat, catFields]) => ({
          cat,
          fields: Object.entries(catFields).sort(([aName, aSpec], [bName, bSpec]) => {
            const ai = aSpec.index ?? Number.MAX_SAFE_INTEGER;
            const bi = bSpec.index ?? Number.MAX_SAFE_INTEGER;
            if (ai !== bi) return ai - bi;
            return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
          }),
        })),
    [templateFields],
  );

  const buildInitialValues = useCallback((): FormValues => {
    const base: FormValues = {};
    // Template defaults only apply to a brand-new job; when editing an existing
    // job/draft/copy, an empty value was cleared on purpose and must stay empty
    const isEditing = selectedJob != null;

    for (const [cat, catFields] of Object.entries(templateFields)) {
      base[cat] = {};

      for (const [fname, spec] of Object.entries(catFields)) {
        const raw = getJobRawValue(selectedJob, cat, fname);
        let initialValue: unknown;

        if (['image', 'audio', 'video'].includes(spec.type)) {
          if (Array.isArray(spec.options) && spec.options.length > 0) {
            initialValue =
              isEditing && (raw == null || raw === '') ? null : buildOptionMediaValue(spec, raw);
          } else {
            initialValue = buildProjectMediaValue(raw);
          }
        } else {
          if (typeof raw === 'string') initialValue = raw;
          else if (raw != null) initialValue = String(raw);
          else initialValue = isEditing ? '' : (spec.value ?? '');
        }

        base[cat][fname] = initialValue;
      }
    }

    return base;
  }, [templateFields, selectedJob]);

  const [values, setValues] = useState<FormValues>(() => buildInitialValues());
  const [formats, setFormats] = useState<Set<string>>(() => new Set(selectedJob?.formats ?? []));
  const [versionName, setVersionName] = useState<string>(() => selectedJob?.name || '');

  // re-hydrate when selection changes
  useEffect(() => {
    setSubmitting(false);
    setError(null);
    setNameError(null);
    setFormatsError(null);
    setSuccess(false);

    setVersionName(selectedJob?.name || '');
    setFormats(new Set(selectedJob?.formats ?? []));
    setValues(buildInitialValues());
  }, [selectedJob, buildInitialValues]);

  const setField = (cat: string, fname: string, val: unknown) => {
    setValues((prev) => ({ ...prev, [cat]: { ...prev[cat], [fname]: val } }));
  };

  const toggleFormat = (fmt: string) => {
    setFormats((prev) => {
      const next = new Set(prev);
      next.has(fmt) ? next.delete(fmt) : next.add(fmt);
      return next;
    });
    setFormatsError(null);
  };

  const setAllFormats = (selectAll: boolean) => {
    setFormats(selectAll ? new Set(allFormats) : new Set());
    setFormatsError(null);
  };

  // ---------- Validation ----------
  const validateAll = (): boolean => {
    const vErr = validateVersionName(versionName);
    const fErr = validateFormats(Array.from(formats));

    setNameError(vErr);
    setFormatsError(fErr);

    return !vErr && !fErr;
  };

  // ---------- Success countdown ----------
  useEffect(() => {
    if (!success) return;

    setCountdown(5);
    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          goBack();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [success, goBack]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError(null);
    if (!validateAll()) return;
    setSubmitting(true);

    const normalized: Record<string, Record<string, Record<string, unknown>>> = {};
    for (const [cat, catFields] of Object.entries(templateFields)) {
      normalized[cat] = {};
      for (const fname of Object.keys(catFields)) {
        const v = values?.[cat]?.[fname] as Record<string, unknown> | string | undefined;
        normalized[cat][fname] = {
          set_value: (v && typeof v === 'object' ? (v as Record<string, unknown>).name : String(v ?? '')),
          type: catFields[fname].type,
          option: !!catFields[fname].options,
          key: (v && typeof v === 'object' ? (v as Record<string, unknown>).key : undefined),
          id: (v && typeof v === 'object' ? (v as Record<string, unknown>).id : undefined),
        };
      }
    }

    // The API drops media whose storage key belongs to another workspace and the robot then
    // renders without it, so stop here and name the files instead (e.g. a stale list after a switch).
    if (currentGroupId) {
      const foreign = Object.values(normalized).flatMap((catFields) =>
        Object.values(catFields)
          .filter((f) => typeof f.key === 'string' && !keyBelongsToWorkspace(f.key, currentGroupId))
          .map((f) => String(f.set_value || f.id || 'file')),
      );
      if (foreign.length) {
        setError(`These files are stored in another workspace and can't be used: ${foreign.join(', ')}. Pick files from this project instead.`);
        setSubmitting(false);
        return;
      }
    }

    const result = await createJob({
      name: versionName.trim(),
      formats: Array.from(formats),
      fields: normalized,
    });
    if (result.success) {
      setSuccess(true);
    } else {
      console.error('Error creating job:', result.error);
      setError(result.error || 'An error occurred while creating the job.');
      setSubmitting(false);
    }
  };

  const onSaveDraft = () => {
    if (readOnly) return;
    saveDraft({
      name: (versionName || 'Untitled draft').trim(),
      formats: Array.from(formats),
      fields: values,
    });
    goBack();
  };

  // ---------- Render success view ----------
  if (success) {
    return (
      <div className="flex justify-center items-center px-5 py-10 text-center">
        <div>
          <h2 className="mt-4 mb-1 text-foreground">The job has been moved to processing.</h2>
          <p className="mt-2 mb-6 text-muted-foreground">Follow its status on the template page.</p>
          <p className="mt-4 mb-4 text-muted-foreground">Go back to the template page</p>
          <button
            type="button"
            className="rounded-md bg-primary px-5 py-2.5 font-semibold text-primary-foreground transition-colors hover:bg-primary-hover"
            onClick={goBack}
          >
            Back ({countdown})
          </button>
        </div>
      </div>
    );
  }

  // ---------- Render form ----------
  return (
    <form
      className={`grid gap-4 ${readOnly ? 'pointer-events-auto' : ''}`}
      onSubmit={onSubmit}
      noValidate
    >
      {error && (
        <div className="text-destructive text-sm -mt-1">{error}</div>
      )}

      {/* Version name */}
      <div className="grid gap-2 grid-cols-[130px_auto] max-md:grid-cols-1 max-md:gap-1.5">
        <label className="grid items-center" htmlFor="versionName">
          Version name
        </label>
        <div className="grid gap-1.5">
          <input
            id="versionName"
            className={`border rounded-md bg-card px-3 py-2.5 text-sm outline-none transition-colors focus:ring-2 focus:ring-ring/30 ${
              nameError ? 'border-destructive' : 'border-border/60'
            }`}
            type="text"
            value={versionName}
            placeholder="Version name"
            onChange={(e) => {
              setVersionName(e.target.value);
              if (nameError) setNameError(null);
            }}
            onBlur={() => setNameError(validateVersionName(versionName))}
            maxLength={120}
            disabled={readOnly}
          />
          {!readOnly && nameError && (
            <div className="text-destructive text-xs -mt-1">{nameError}</div>
          )}
        </div>
      </div>

      {/* Formats */}
      <div className="grid gap-2 grid-cols-[130px_auto] max-md:grid-cols-1 max-md:gap-1.5">
        <label className="grid items-center">Formats</label>
        <div className="grid gap-1.5">
          <MultiSelectDropdown
            options={allFormats}
            selected={formats}
            onToggle={toggleFormat}
            onSetAll={setAllFormats}
            disabled={readOnly}
            placeholder="Select formats"
          />
          {!readOnly && formatsError && (
            <div className="text-destructive text-xs -mt-1">{formatsError}</div>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="grid gap-3">
        {sortedCategories.map(({ cat, fields }) => (
          <Accordion key={cat} title={cat}>
            <ul className="list-none p-0 m-0 grid gap-2">
              {fields.map(([fname, spec]) => {
                const val = values?.[cat]?.[fname] ?? '';
                return (
                  <li
                    key={fname}
                    className="grid grid-cols-[240px_1fr] gap-2.5 items-center max-md:grid-cols-1 max-md:gap-1.5 max-md:items-start"
                  >
                    <label className="font-medium whitespace-normal self-start pt-2.5">
                      {fname}
                    </label>
                    <div>
                      <FieldRenderer
                        spec={spec}
                        value={val as string | null}
                        onChange={(v) => setField(cat, fname, v)}
                        disabled={readOnly}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </Accordion>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2.5 mt-1.5 max-md:flex-col-reverse max-md:[&_button]:w-full">
        {readOnly ? (
          <>
            <button
              type="button"
              className="rounded-md border border-border/60 bg-card px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-secondary"
              onClick={() => createCopyFrom(selectedJob)}
            >
              Create copy
            </button>
            <button
              type="button"
              className="rounded-md border border-border/60 bg-card px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-secondary"
              onClick={goBack}
            >
              Back
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="mr-auto rounded-md border border-border/60 bg-card px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-secondary disabled:opacity-50"
              onClick={onSaveDraft}
              disabled={submitting}
            >
              Save draft
            </button>
            <button
              type="button"
              className="rounded-md border border-border/60 bg-card px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-secondary disabled:opacity-50"
              onClick={goBack}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-hover disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </>
        )}
      </div>
    </form>
  );
}
